// src/components/layout/Footer.js
import React, { useMemo } from 'react'
import {
    Box,
    Container,
    Grid,
    Typography,
    Divider,
    IconButton,
    Paper,
    List,
    ListItem,
} from '@mui/material'
import {
    Facebook,
    Twitter,
    Instagram,
    PhoneAndroid,
    Email,
    LocationOn,
    Apple,
    Android,
} from '@mui/icons-material'
import { motion } from 'framer-motion'
import { Link as RouterLink } from 'react-router-dom'
import { useLanguage } from '../../contexts/LanguageContext'

const Footer = () => {
    const { t, lang } = useLanguage()
    const isFr = lang === 'fr'
    const currentYear = new Date().getFullYear()

    const footerSections = useMemo(
        () => [
            {
                title: t('footer.section.services', null, 'Services'),
                links: [
                    { label: t('footer.link.send', null, 'Send Money'), to: '/send' },
                    { label: isFr ? 'Paiement' : 'Payment', to: '/payment' },
                    { label: isFr ? 'Payer des factures' : 'Pay Bills', to: '/pay-bills' },
                    { label: t('footer.link.bank', null, 'Bank Transfers'), to: '/bank-transfers' },
                ],
            },
            {
                title: t('footer.section.support', null, 'Support'),
                links: [
                    { label: t('footer.link.help', null, 'Help Center'), to: '/help-center' },
                    { label: t('footer.link.contact', null, 'Contact Us'), to: '/contact' },
                    { label: t('footer.link.faq', null, 'FAQ'), to: '/faq' },
                ],
            },
            {
                title: t('footer.section.company', null, 'Company'),
                links: [
                    { label: t('footer.link.about', null, 'About Us'), to: '/about' },
                    { label: t('footer.link.careers', null, 'Careers'), to: '/careers' },
                    { label: t('footer.link.press', null, 'Press'), to: '/press' },
                    { label: t('footer.link.blog', null, 'Blog'), to: '/blog' },
                ],
            },
            {
                title: t('footer.section.legal', null, 'Legal'),
                links: [
                    { label: t('footer.link.privacy', null, 'Privacy Policy'), to: '/legal/privacy' },
                    { label: t('footer.link.terms', null, 'Terms of Service'), to: '/legal/terms' },
                    { label: t('footer.link.compliance', null, 'Compliance'), to: '/legal/compliance' },
                    { label: t('footer.link.kyc', null, 'KYC Policy'), to: '/legal/kyc' },
                ],
            },
        ],
        [isFr, t]
    )

    const socialLinks = [
        { icon: Facebook, href: '#', label: 'Facebook' },
        { icon: Twitter, href: '#', label: 'Twitter' },
        { icon: Instagram, href: '#', label: 'Instagram' },
    ]

    const contactInfo = [
        { icon: PhoneAndroid, text: '+1 (555) 123-4567' },
        { icon: Email, text: 'support@mojopayr.com' },
        { icon: LocationOn, text: 'Owings Mills MD, NY, USA' },
    ]

    const appDownloads = [
        {
            icon: Apple,
            eyebrow: isFr ? 'Telecharger sur' : 'Download on the',
            label: 'App Store',
            href: '#',
        },
        {
            icon: Android,
            eyebrow: isFr ? 'Disponible sur' : 'Get it on',
            label: 'Google Play',
            href: '#',
        },
    ]

    return (
        <Paper
            component="footer"
            elevation={0}
            sx={{
                mt: 0,
                bgcolor: '#0B0620',
                borderRadius: 0,
            }}
        >
            <Container maxWidth="lg">
                <Box sx={{ py: 6 }}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={4}>
                            <motion.div>
                                <Typography
                                    variant="h6"
                                    gutterBottom
                                    fontWeight="bold"
                                    sx={{ color: 'white' }}
                                >
                                    MojoPayr
                                </Typography>

                                <Typography
                                    variant="body2"
                                    sx={{ color: 'rgba(255,255,255,0.70)' }}
                                    paragraph
                                >
                                    {t(
                                        'footer.about',
                                        null,
                                        'Fast, secure, and affordable money transfers to Sierra Leone, Liberia, and Guinea. Connecting families and businesses across borders.'
                                    )}
                                </Typography>

                                <Box sx={{ mt: 3 }}>
                                    {contactInfo.map((contact, index) => (
                                        <Box
                                            key={index}
                                            sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                                        >
                                            <contact.icon
                                                sx={{
                                                    fontSize: 16,
                                                    color: 'rgba(255,255,255,0.65)',
                                                    mr: 1,
                                                }}
                                            />
                                            <Typography
                                                variant="body2"
                                                sx={{ color: 'rgba(255,255,255,0.65)' }}
                                            >
                                                {contact.text}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </motion.div>
                        </Grid>

                        {footerSections.map((section, index) => (
                            <Grid item xs={12} sm={6} md={2} key={index}>
                                <Typography
                                    variant="h6"
                                    gutterBottom
                                    fontWeight="bold"
                                    sx={{ color: 'white' }}
                                >
                                    {String(section.title).toUpperCase()}
                                </Typography>

                                <List dense sx={{ m: 0, p: 0 }}>
                                    {section.links.map((link, linkIndex) => (
                                        <ListItem key={linkIndex} sx={{ px: 0, py: 0.5 }}>
                                            <Typography
                                                component={RouterLink}
                                                to={link.to}
                                                sx={{
                                                    textDecoration: 'none',
                                                    color: 'rgba(255,255,255,0.65)',
                                                    fontSize: 14,
                                                    '&:hover': { color: '#FF8A00' },
                                                }}
                                            >
                                                {link.label}
                                            </Typography>
                                        </ListItem>
                                    ))}
                                </List>
                            </Grid>
                        ))}
                    </Grid>

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: { xs: 'flex-start', md: 'center' },
                            gap: 1.5,
                            flexWrap: 'wrap',
                            mt: 3,
                        }}
                    >
                        {appDownloads.map((download) => (
                            <Box
                                key={download.label}
                                component="a"
                                href={download.href}
                                aria-label={`${download.eyebrow} ${download.label}`}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    minWidth: 164,
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: 2,
                                    bgcolor: '#05030D',
                                    border: '1px solid rgba(255,255,255,0.16)',
                                    color: 'white',
                                    textDecoration: 'none',
                                    boxShadow: '0 12px 28px rgba(0,0,0,0.24)',
                                    transition: 'border-color .2s ease, transform .2s ease',
                                    '&:hover': {
                                        borderColor: '#FF8A00',
                                        transform: 'translateY(-1px)',
                                    },
                                }}
                            >
                                <download.icon sx={{ fontSize: 30 }} />
                                <Box sx={{ lineHeight: 1 }}>
                                    <Typography
                                        component="span"
                                        sx={{
                                            display: 'block',
                                            color: 'rgba(255,255,255,0.70)',
                                            fontSize: 10,
                                            fontWeight: 700,
                                        }}
                                    >
                                        {download.eyebrow}
                                    </Typography>
                                    <Typography
                                        component="span"
                                        sx={{
                                            display: 'block',
                                            color: 'white',
                                            fontSize: 17,
                                            fontWeight: 900,
                                        }}
                                    >
                                        {download.label}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.10)' }} />

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 2,
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{ color: 'rgba(255,255,255,0.65)' }}
                        >
                            © {currentYear} MojoPayr. {t('footer.rights', null, 'All rights reserved.')}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {socialLinks.map((social, index) => (
                                <IconButton
                                    key={index}
                                    href={social.href}
                                    size="small"
                                    aria-label={social.label}
                                    sx={{
                                        color: 'rgba(255,255,255,0.65)',
                                        '&:hover': { color: '#FF8A00' },
                                    }}
                                >
                                    <social.icon />
                                </IconButton>
                            ))}
                        </Box>
                    </Box>

                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <Typography
                            variant="caption"
                            sx={{ color: 'rgba(255,255,255,0.55)' }}
                        >
                            {t('footer.licensed', null, 'Licensed Money Transmitter')} | {t('footer.nmls', null, 'NMLS ID: 1234567')} |{' '}
                            <Typography
                                component={RouterLink}
                                to="/legal/regulatory"
                                sx={{
                                    color: '#FF8A00',
                                    textDecoration: 'none',
                                    fontWeight: 800,
                                    display: 'inline',
                                }}
                            >
                                {t('footer.regulatory', null, 'Regulatory Information')}
                            </Typography>
                        </Typography>
                    </Box>
                </Box>
            </Container>
        </Paper>
    )
}

export default Footer
